<template>
	<div class="divide-gray-200 divide-y overflow-y-auto">
		<ui-alert-errors
			v-if="otherErrorMessages.length > 0"
			:messages="otherErrorMessages"
		/>

		<bid-section
			:label="reference.trade == 'SELL' ? 'Seller' : 'Buyer'"
			:show-reference="showReference"
		>
			<template v-slot:bid></template>
			<template v-slot:reference>{{
				$cropOptions.getLocalizedLabel(
					counterpart.type,
					$cropOptions.companyTypeOptions
				)
			}}</template>
		</bid-section>

		<bid-section label="Goods" :show-reference="showReference">
			<template v-slot:bid>
				<crop-product-summary :tradable="form" />
			</template>
			<template v-slot:reference>
				<crop-product-summary :tradable="reference" />
			</template>
		</bid-section>

		<bid-section label="Quality" :show-reference="showReference">
			<template v-slot:bid>
				<bid-section-toggleable>
					<template v-slot:closed>
						<crop-quality-summary :tradable="form" />
					</template>
					<template v-slot:open>
						<offer-edit-quality :form="form" grid-cols="1" />
					</template>
				</bid-section-toggleable>
			</template>
			<template v-slot:reference>
				<crop-quality-summary
					:tradable="reference"
					:reference-tradable="form"
				/>
			</template>
		</bid-section>

		<bid-section label="Quantity" :show-reference="showReference">
			<template v-slot:bid>
				<div class="-mt-1 grid gap-x-6 grid-cols-2">
					<ui-input-group
						class
						v-model="form.qty.mt"
						field="qty.mt"
						label="Quantity"
						suffix="t"
						:form="form"
						:required="true"
						:no-label="true"
					></ui-input-group>
					<offer-edit-qty-tolerance :form="form" />
				</div>
			</template>
			<template v-slot:reference>
				<div>
					{{ reference.qty.mt }} t
					<br />
					{{
						reference.qty.partial_ok
							? __("Partial quantity possible")
							: __("Partial quantity not desired")
					}}
				</div>
				<!-- <crop-qty-summary :tradable="reference" /> -->
			</template>
		</bid-section>

		<bid-section label="Price" :show-reference="showReference">
			<template v-slot:bid>
				<!-- negative top margin (-mt-1) because offer-edit-price has a small top margin … why? -->
				<offer-edit-price
					:form="form"
					:show-label="false"
					:active-futures="activeFutures"
					:grid="priceGrid"
					class="-mt-1"
				/>

				<div
					class="max-w-sm bg-gray-100 rounded-lg mt-2 p-2 grid grid-cols-3 gap-x-6 gap-y-2"
					v-if="
						form.location.mode !== 'FCA' &&
						match &&
						match.price_estimate_type == 'RESULT_MINUS_FREIGHT'
					"
				>
					<div class="text-right">
						{{ $cropOptions.formatLocalizedNumber(reference.price.mt) }} €/t
					</div>
					<div class="col-span-2">{{ __("Buyer's DAP Price") }}</div>
					<div class="text-right">
						-
						{{ $cropOptions.formatLocalizedNumber(match.freight_cost_eur) }} €/t
					</div>
					<div class="col-span-2">
						{{
							__("messages.app_freight_charges", {
								args: { distance: match.distance_km_road.toFixed(0) },
							})
						}}
					</div>
					<div class="col-span-3">
						<hr class="border-gray-300" />
					</div>
					<div class="text-right text-orange font-semibold">
						{{ $cropOptions.formatLocalizedNumber(match.price_estimate) }} €/t
					</div>
					<div class="col-span-2 text-orange font-semibold">
						{{ __("Your FCA price") }}
					</div>
				</div>
			</template>
			<template v-slot:reference v-if="showReference">
				<crop-price-summary :tradable="reference" :user="user" :match="match" />
			</template>
		</bid-section>

		<bid-section label="Fulfilment Date" :show-reference="showReference">
			<template v-slot:bid>
				<bid-section-toggleable>
					<template v-slot:closed>{{
						$cropOptions.dateToString(form, locale)
					}}</template>
					<template v-slot:open>
						<offer-edit-date :form="form" grid-cols="2" :no-label="true" />
					</template>
				</bid-section-toggleable>
			</template>
			<template v-slot:reference>{{
				$cropOptions.dateToString(reference, locale)
			}}</template>
		</bid-section>

		<bid-section label="Location" :show-reference="showReference">
			<template v-slot:bid>
				<bid-section-toggleable>
					<template v-slot:closed>{{
						$cropOptions.locationToString(form, locale)
					}}</template>
					<template v-slot:open>
						<offer-edit-location :form="form" :no-label="true" />
					</template>
				</bid-section-toggleable>
			</template>
			<template v-slot:reference>
				<span
					v-if="
						user &&
						user.type == 'FARMER' &&
						form.location.mode == 'FCA' &&
						reference.trade == 'BUY' &&
						reference.location.mode == 'DAP' &&
						reference.location.optional_transport
					"
				>
					{{
						$cropOptions.getLocalizedLabel(
							"FCA",
							$cropOptions.locationModeOptions
						)
					}}
					{{ user.address.zip }} {{ user.address.city }}
				</span>
				<span v-else>
					<!-- {{ $cropOptions.locationToString(reference, locale) }} -->
					{{
						$cropOptions.getLocalizedLabel(
							reference.location.mode,
							$cropOptions.locationModeOptions
						)
					}}
					<br />

					{{ reference.location.address.zip }}
					{{ reference.location.address.city }}
				</span>
			</template>
		</bid-section>

		<bid-section label="Contract Conditions" :show-reference="showReference">
			<template v-slot:bid>
				<bid-section-toggleable>
					<template v-slot:closed>
						<crop-extra-summary :tradable="form" />
					</template>
					<template v-slot:open>
						<offer-edit-extra
							:form="form"
							:no-label="true"
							:user="user"
							:reference="reference"
							:counterpart="counterpart"
						/>
					</template>
				</bid-section-toggleable>
			</template>
			<template v-slot:reference>
				<div class="hyphens">
					<crop-extra-summary :tradable="reference" />
				</div>
			</template>
		</bid-section>

		<div
			class="grid px-4 sm:px-6 py-4 grid-cols-12 gap-x-8 gap-y-4 items-center"
		>
			<!--
                <ui-textarea-group
                class="col-span-11 sm:col-span-6 md:col-span-7"
                v-model="form.message"
                field="message"
                label="Comment"
                :form="form"
                :required="false"
                input-id="extra-message"
                _uid="extra-message"
            ></ui-textarea-group>
            -->

			<ui-select-group
				class="col-span-11 sm:col-span-6 md:col-start-9 md:col-end-13"
				v-model="form.extra.expiry_option"
				type="select"
				field="extra.expiry_option"
				label="Validity"
				:options="
					$cropOptions.bidExpiryOptions.filter((o) => !o.paperOnly === true)
				"
				:form="form"
				:required="true"
				input-id="expiry-option"
			></ui-select-group>
		</div>
	</div>
</template>

<script>
export default {
	props: {
		form: {
			type: Object,
			required: true,
		},
		counterpart: {
			type: Object,
			required: false,
		},
		reference: {
			type: Object,
			required: true,
		},
		match: {
			type: Object,
			required: false,
		},
		iframeModal: {
			type: Boolean,
			default: false,
		},
		trade: {
			type: String,
			required: true,
		},
		activeFutures: {
			type: Array,
			required: true,
		},
		otherErrors: {
			type: Object,
			required: false,
		},
		showReference: {
			type: Boolean,
			default: true,
		},
		user: {
			type: Object,
			required: false,
		},
	},
	data() {
		return {
			// showReference: true,
		};
	},
	computed: {
		otherErrorMessages() {
			return Object.values(this.otherErrors || {});
		},
		priceGrid() {
			return {
				cols: "5",
				flat: {
					colSpan: [2],
				},
				indexed: {
					colSpan: [3, 2],
				},
			};
		},
	},
};
</script>

<style scoped>
.hyphens {
	-webkit-hyphens: auto;
	-ms-hyphens: auto;
	hyphens: auto;
}
</style>
